import { useEffect, useState } from 'react';
import { Form, Input, Button, Modal } from 'antd';
import {
  LockOutlined,
  ArrowLeftOutlined,
  KeyOutlined,
  CheckCircleFilled
} from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import endpoints from '../../api/endpoints';
import { notifyError } from '../../utils/notify';

// Stage 5N-6: tenant Reset Password page.
//
// The token is read from ?token=... query param. If absent we render
// an explicit "invalid reset link" surface — explicit because the user
// already holds (or doesn't hold) a link, so account enumeration is
// not at risk on this page.
//
// Strong-password rules mirror the backend (min 12, upper, lower,
// digit, symbol) — the backend will reject anyway, but pre-validating
// keeps the round-trip fast.
function AuthBadge({ icon, className = '' }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex items-center justify-center rounded-xl bg-brand-500 text-white shadow-[0_8px_20px_-6px_rgba(24,119,242,0.55)] text-2xl ${className}`}
    >
      {icon}
    </span>
  );
}

const STRENGTH_RULES = [
  { test: (p) => p.length >= 12, label: 'at least 12 characters' },
  { test: (p) => /[A-Z]/.test(p), label: 'uppercase letter' },
  { test: (p) => /[a-z]/.test(p), label: 'lowercase letter' },
  { test: (p) => /[0-9]/.test(p), label: 'digit' },
  { test: (p) => /[^A-Za-z0-9]/.test(p), label: 'symbol' }
];

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [submitting, setSubmitting] = useState(false);
  const [pwd, setPwd] = useState('');
  const [form] = Form.useForm();

  // Show the success modal -> redirect to /login on close.
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    if (!successOpen) return;
    // Auto-redirect after a brief pause so the user reads the message.
    const t = setTimeout(() => {
      navigate('/login', { replace: true });
    }, 2200);
    return () => clearTimeout(t);
  }, [successOpen, navigate]);

  async function onFinish(values) {
    setSubmitting(true);
    try {
      await endpoints.auth.resetPassword({
        token,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword
      });
      setSuccessOpen(true);
    } catch (err) {
      notifyError(err, { title: 'Could not reset password' });
    } finally {
      setSubmitting(false);
    }
  }

  const remainingRules = STRENGTH_RULES.filter((r) => !r.test(pwd || ''));

  // ------------------------ no-token state ----------------------------
  if (!token) {
    return (
      <div className="gi-onboarding-rise w-full bg-white rounded-2xl shadow-elevated border border-surface-200 p-6 sm:p-8">
        <AuthBadge className="h-11 w-11" icon={<KeyOutlined />} />
        <div className="mt-5 mb-3">
          <h2 className="text-2xl sm:text-3xl font-bold text-ink-900 m-0 leading-tight tracking-tight">
            Invalid reset link
          </h2>
          <p className="text-sm text-ink-500 m-0 mt-1.5">
            This password reset link is missing or malformed. Please request a
            new one from the login page.
          </p>
        </div>
        <div className="mt-6 pt-4 border-t border-surface-200 flex justify-center">
          <Link
            to="/forgot-password"
            className="group inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 text-white text-sm font-semibold hover:bg-brand-600 active:bg-brand-700 transition-colors shadow-sm hover:shadow"
          >
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  // ------------------------ form state --------------------------------
  return (
    <>
      <div className="gi-onboarding-rise w-full bg-white rounded-2xl shadow-elevated border border-surface-200 p-6 sm:p-8">
        <AuthBadge className="h-11 w-11" icon={<KeyOutlined />} />

        <div className="mt-5 mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-ink-900 m-0 leading-tight tracking-tight">
            Reset password
          </h2>
          <p className="text-sm text-ink-500 m-0 mt-1.5">
            Set a new password for your Glass-i account.
          </p>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          disabled={submitting}
          size="large"
          className="onboarding-compact-form"
        >
          <Form.Item
            label={<span className="text-sm font-semibold text-ink-800">New password</span>}
            name="newPassword"
            rules={[
              { required: true, message: 'new password is required' },
              { min: 12, message: 'must be at least 12 characters' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="text-ink-400" />}
              autoComplete="new-password"
              placeholder="At least 12 characters"
              onChange={(e) => setPwd(e.target.value || '')}
            />
          </Form.Item>

          <Form.Item
            label={<span className="text-sm font-semibold text-ink-800">Confirm password</span>}
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'confirm password is required' },
              ({ getFieldValue }) => ({
                validator(_rule, value) {
                  if (!value || value === getFieldValue('newPassword')) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('passwords do not match'));
                }
              })
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="text-ink-400" />}
              autoComplete="new-password"
              placeholder="Repeat the new password"
            />
          </Form.Item>

          {pwd ? (
            <ul className="text-xs text-ink-500 mb-4 mt-1 space-y-0.5 list-none p-0">
              {STRENGTH_RULES.map((r) => {
                const ok = r.test(pwd);
                return (
                  <li
                    key={r.label}
                    className={ok ? 'text-emerald-600' : 'text-ink-400'}
                  >
                    {ok ? '✓ ' : '• '} {r.label}
                  </li>
                );
              })}
            </ul>
          ) : null}

          <Form.Item className="!mb-0 !mt-1">
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              block
              disabled={submitting || remainingRules.length > 0}
              className="!h-12 !text-base !font-semibold !shadow-[0_8px_20px_-8px_rgba(24,119,242,0.55)] hover:!shadow-[0_10px_24px_-8px_rgba(24,119,242,0.65)] active:!shadow-[0_4px_12px_-4px_rgba(24,119,242,0.55)] transition-shadow"
            >
              Reset password
            </Button>
          </Form.Item>
        </Form>

        <div className="mt-5 pt-4 border-t border-surface-200 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-500 hover:text-brand-600 transition-colors"
          >
            <ArrowLeftOutlined />
            Back to login
          </Link>
        </div>
      </div>

      <Modal
        open={successOpen}
        closable={false}
        footer={null}
        centered
        maskClosable={false}
      >
        <div className="flex flex-col items-center text-center py-2">
          <CheckCircleFilled className="text-5xl text-emerald-500 mb-3" />
          <h3 className="text-xl font-bold text-ink-900 m-0">
            Password reset successful
          </h3>
          <p className="text-sm text-ink-500 mt-2 mb-0">
            Please sign in again with your new password.
          </p>
        </div>
      </Modal>
    </>
  );
}
